import { CardTypeEnum } from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import useFetchCardAndUserDetails from 'features/cardManagement/hooks/useFetchCardAndUserDetails';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useEngagement } from 'shared/hooks/useBraze';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';

type Params = {
  title: string;
  message: string;
  resultType: string;
  cardId: string;
  defaultSuccess?: string;
  cardType: string;
  shouldShowWalletButton?: string;
};
const CardActivationSuccessScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'activationCard',
  });

  const {
    cardId,
    title,
    message,
    defaultSuccess,
    cardType,
    shouldShowWalletButton = 'true',
  } = useLocalSearchParams<Params>();

  const { refreshTiles, fetching: loadingRefreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHARGE_CARD,
    HomeTileTypeIds.NEXT_BEST_ACTION_TILE,
  ]);

  const handleOnSetPinButtonAnalytics = usePressableAnalytics({
    analyticsId: 'setPinClicked',
    customAttributes: {
      card_type: cardType?.toLowerCase(),
    },
  });

  const handleOnSkipPinButtonAnalytics = usePressableAnalytics({
    analyticsId: 'setPinSkipped',
    customAttributes: {
      card_type: cardType?.toLowerCase(),
    },
  });

  const { cardDetails, error, fetching } = useFetchCardAndUserDetails({
    cardId,
  });

  const walletProps = {
    cardId: cardId,
    maskedCardNumber: cardDetails?.maskedCardNumber ?? '',
    cardholderName: cardDetails?.cardHolderName?.full ?? '',
    isDebit: cardDetails?.type === CardTypeEnum.DebitCard,
    analyticsContextOverride: {
      category: 'card',
      subCategory: 'wallet_provisioning',
      sourceName: 'card_activated_page',
    },
    sentryPageName: 'Card Activation Success Screen',
  };
  const { requestedCard, setRequestedCardId } = useEngagement();

  useEffect(() => {
    setRequestedCardId?.('card_activation_success_screen_offer');
  }, [setRequestedCardId]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  useViewDidFocusScreenAnalyticsEvent({
    key: defaultSuccess ? 'Card Activated' : 'PIN Set Completed',
    customAttributes: {
      card_type: cardType?.toLowerCase(),
    },
  });

  if (loadingRefreshTiles) {
    // TODO: https://varomoney.atlassian.net/browse/JT-133729
    // found a race condition while testing, adding this quick work around
    // tracked a ticket to fix this properly
    return null;
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      {defaultSuccess ? (
        <SuccessScreen
          visual={
            (cardType as CardTypeEnum) === CardTypeEnum.DebitCard
              ? 'DEBIT_CARD_A_ANIMATED'
              : 'BELIEVE_CARD_A_ANIMATED'
          }
          titleText={translatedText('defaultSuccessTitle')}
          productOfferCard={requestedCard}
          content={
            <BodySmText style={styles.text} numberOfLines={0}>
              {translatedText(
                (cardType as CardTypeEnum) === CardTypeEnum.DebitCard
                  ? 'defaultSuccessMessage'
                  : 'defaultBelieveSuccessMessage',
              )}
            </BodySmText>
          }
          primaryButton={{
            label: translatedText('setPin'),
            onPress: () => {
              handleOnSetPinButtonAnalytics();
              router.replace({
                pathname: `/myvaro/card/new-pin/[cardId]`,
                params: {
                  cardId: cardId,
                },
              });
            },
          }}
          secondaryButton={{
            label: translatedText('notRightNow'),
            onPress: () => {
              handleOnSkipPinButtonAnalytics();
              router.replace(`/`);
            },
          }}
          addWalletButton={
            shouldShowWalletButton === 'true' && !error
              ? walletProps
              : undefined
          }
        />
      ) : (
        <>
          <SuccessScreen
            visual="CARD_A_ANIMATED"
            titleText={title}
            content={
              <BodySmText style={styles.text} numberOfLines={0}>
                {message}
              </BodySmText>
            }
            productOfferCard={requestedCard}
            primaryButton={{
              label: translatedText('done'),
              onPress: () => {
                router.replace(`/myvaro/card/${cardId}`);
              },
              analyticsId: 'goToManageCardClicked',
              customAttributes: {
                card_type: cardType?.toLowerCase(),
              },
            }}
            addWalletButton={
              shouldShowWalletButton === 'true' && !error
                ? walletProps
                : undefined
            }
          />
          <ScreenTransitionOverlay
            isLoading={fetching}
            LoadingComponent={<LoadingSpinner />}
            ErrorComponent={null}
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  text: {
    textAlign: 'center',
  },
});

export default CardActivationSuccessScreen;
